import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import {
  getAccessibleClientsForUser,
  getRegionsForClient,
  getTeamsForRegion,
  readManagerSelection,
  writeManagerSelection,
} from '../utils/managerAccess';

function selectionFromParams(searchParams) {
  return {
    client: searchParams.get('client') || '',
    region: searchParams.get('region') || '',
    team: searchParams.get('team') || '',
    user: searchParams.get('user') || '',
  };
}

function applySelectionToParams(base, selection) {
  const next = new URLSearchParams(base);
  ['client', 'region', 'team', 'user'].forEach((key) => {
    if (selection[key]) next.set(key, selection[key]);
    else next.delete(key);
  });
  return next;
}

export function useManagerHierarchy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isManagerRoute = location.pathname === '/manager';
  const isHierarchyActive = isManagerRoute || (
    location.pathname.startsWith('/bootcamp/') && !!searchParams.get('client')
  );

  const clientId = searchParams.get('client') || '';
  const regionId = searchParams.get('region') || '';
  const teamId = searchParams.get('team') || '';
  const userId = searchParams.get('user') || '';

  const [storedSelection, setStoredSelection] = useState(readManagerSelection);
  const [clients, setClients] = useState([]);
  const [regions, setRegions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [hasClientScope, setHasClientScope] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionUserId, setSessionUserId] = useState(null);

  const selectionRef = useRef({ clientId, regionId, teamId, userId });
  selectionRef.current = { clientId, regionId, teamId, userId };

  const commitSelection = useCallback((nextSelection, { navigateToManager = false } = {}) => {
    writeManagerSelection(nextSelection);
    setStoredSelection(nextSelection);
    if (isHierarchyActive || navigateToManager) {
      if (navigateToManager && !isManagerRoute) {
        const params = applySelectionToParams(new URLSearchParams(), nextSelection);
        const search = params.toString();
        navigate(search ? `/manager?${search}` : '/manager');
        return;
      }
      setSearchParams((prev) => applySelectionToParams(prev, nextSelection), { replace: true });
    }
  }, [isHierarchyActive, isManagerRoute, navigate, setSearchParams]);

  const setClient = useCallback((id) => {
    commitSelection({ client: id || '', region: '', team: '', user: '' }, { navigateToManager: true });
  }, [commitSelection]);

  const setRegion = useCallback((id) => {
    const { clientId: currentClientId } = selectionRef.current;
    commitSelection({ client: currentClientId, region: id || '', team: '', user: '' }, { navigateToManager: true });
  }, [commitSelection]);

  const setTeam = useCallback((id) => {
    const { clientId: currentClientId, regionId: currentRegionId } = selectionRef.current;
    commitSelection({
      client: currentClientId,
      region: currentRegionId,
      team: id || '',
      user: '',
    }, { navigateToManager: true });
  }, [commitSelection]);

  const setUser = useCallback(() => {}, []);

  useEffect(() => {
    if (!isHierarchyActive) return;
    const stored = readManagerSelection();
    const sanitizedStored = stored.user
      ? { ...stored, user: '' }
      : stored;
    if (stored.user) {
      writeManagerSelection(sanitizedStored);
      setStoredSelection(sanitizedStored);
    }
    const fromUrl = selectionFromParams(searchParams);
    if (fromUrl.user) {
      setSearchParams((prev) => applySelectionToParams(prev, { ...fromUrl, user: '' }), { replace: true });
      return;
    }
    const hasUrl = fromUrl.client || fromUrl.region || fromUrl.team;
    if (!hasUrl && sanitizedStored.client) {
      setSearchParams((prev) => applySelectionToParams(prev, sanitizedStored), { replace: true });
    }
  }, [isHierarchyActive, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isHierarchyActive) {
      if (!isManagerRoute) setLoading(false);
      return undefined;
    }

    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const uid = sessionData?.session?.user?.id;
        if (!uid) {
          if (mounted) setError('Unauthorized');
          return;
        }
        if (!mounted) return;
        setSessionUserId(uid);
        const list = await getAccessibleClientsForUser(uid);
        if (!mounted) return;
        setClients(list);
      } catch {
        if (mounted) setError('Failed to load clients');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [isHierarchyActive, isManagerRoute]);

  useEffect(() => {
    if (!isManagerRoute || clientId || clients.length !== 1) return;
    commitSelection({ client: String(clients[0].id), region: '', team: '', user: '' });
  }, [isManagerRoute, clientId, clients, commitSelection]);

  useEffect(() => {
    if (!isHierarchyActive || !sessionUserId || !clientId) {
      setRegions([]);
      setHasClientScope(false);
      return undefined;
    }

    let mounted = true;
    getRegionsForClient(sessionUserId, clientId).then(({ regions: regionList, hasClientScope: scope }) => {
      if (!mounted) return;
      setRegions(regionList);
      setHasClientScope(scope);
    });
    return () => { mounted = false; };
  }, [isHierarchyActive, sessionUserId, clientId]);

  useEffect(() => {
    if (!isManagerRoute || !clientId || regionId || regions.length !== 1) return;
    commitSelection({ client: clientId, region: String(regions[0].id), team: '', user: '' });
  }, [isManagerRoute, clientId, regionId, regions, commitSelection]);

  useEffect(() => {
    if (!isHierarchyActive || !clientId || !regionId) {
      setTeams([]);
      return undefined;
    }

    let mounted = true;
    getTeamsForRegion(clientId, regionId).then((teamList) => {
      if (!mounted) return;
      setTeams(teamList);
    });
    return () => { mounted = false; };
  }, [isHierarchyActive, clientId, regionId]);

  useEffect(() => {
    if (!isManagerRoute || !teamId) return;
    const { clientId: currentClientId, regionId: currentRegionId } = selectionRef.current;
    commitSelection({ client: currentClientId, region: currentRegionId, team: '', user: '' });
  }, [isManagerRoute, teamId, commitSelection]);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.id) === String(clientId)) || null,
    [clients, clientId]
  );
  const selectedRegion = useMemo(
    () => regions.find((r) => String(r.id) === String(regionId)) || null,
    [regions, regionId]
  );
  const selectedTeam = useMemo(
    () => teams.find((t) => String(t.id) === String(teamId)) || null,
    [teams, teamId]
  );
  return {
    loading,
    error,
    clients,
    regions,
    teams,
    users: [],
    hasClientScope,
    clientId,
    regionId,
    teamId,
    userId: '',
    selectedClient,
    selectedRegion,
    selectedTeam,
    selectedUser: null,
    showClientPicker: clients.length > 1,
    showRegionPicker: regions.length > 1,
    showTeamPicker: teams.length > 1,
    showUserPicker: false,
    setClient,
    setRegion,
    setTeam,
    setUser,
    storedSelection,
  };
}
